import { useEffect, useRef, useState } from "react";
import { useSorting } from "../../contexts/SortingContext";
import { HTTP_RESPONSE_CODES } from "../../globals/globals";
import { dropdownConfig } from "../../services/dropdownsConfigService";
import "./FiltersAndSorting.scss";
import Dropdowns from "../Dropdowns/Dropdowns";
import { Tooltip } from "@mui/material";

// filters configuration example:
// [

//     {
//       id: 116, => the id of the dropdown that you want.(MANDATORY)
//       columnName: "orderNumber", => columnName that you will send to the backend and is obviously releated to this filter.(MANDATORY)
//       columnSelection: "id", => column that you need to get from the dropdown to send to the backend like id , name , status etc.(MANDATORY)
//       isDate: false, => soon this will used to render a date filter like datepicker or date range.(Optional)
//       parameters: orderParameter, => if your dropdown requires a parameter put here , to prevent error the parameter need to be a useState.(Optional)
//       preselectValues: [2, 19,6], if you need to preselect some options of the dropdown, must match whit columnPreselection that you configure in your dropdown (DB). (Optional)
//     },
    
//   ]


export const FiltersAndSorting = ({
  showHeaderOrder = true,
  hideArrows = false,
  onFiltersChange,
  viewFilters = false,
}) => {
  const {
    order,
    loopKey,
    isAsc,
    isDesc,
    properties,
    columnFilters,
    filtersList,
  } = useSorting();
  const [filtersResult, setFiltersResult] = useState({});
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [preselectMap, setPreselectMap] = useState({});
  const [resetCounter, setResetCounter] = useState(0);
  const [clearAllDropdowns, setClearAllDropdowns] = useState(null);

  //   functions

  /**
   * The function `applyFilterSelection` updates selected filters based on column name, column selection,
   * and selected items, and triggers a callback function to notify of the changes.
   */
  const applyFilterSelection = (columnName, columnSelection, selectedItems) => {
    const values = selectedItems?.map((item) => item[columnSelection]) || [];

    const updated = values.length
      ? [
          ...selectedFilters.filter((f) => f.columnName !== columnName),
          { columnName, value: values },
        ]
      : selectedFilters.filter((f) => f.columnName !== columnName);

    setSelectedFilters(updated);
    onFiltersChange?.(updated);

    setPreselectMap((prev) => ({
      ...prev,
      [columnName]: {
        value: values,
        resetId: resetCounter,
      },
    }));
  };
  /**
   * The `clearFilter` function removes a filter based on the specified column name and updates the
   * selected filters.
   */

  const clearFilter = (columnName) => {
    const updated = selectedFilters.filter((f) => f.columnName !== columnName);
    setSelectedFilters(updated);
    onFiltersChange?.(updated);
  };
  /**
   * The `clearAllFilters` function resets all filters, updates the preselect map, and triggers an
   * `onFiltersChange` event with an empty array.
   */

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setResetCounter((prev) => prev + 1);

    const clearedMap = {};
    filtersList.forEach((filter) => {
      clearedMap[filter.columnName] = {
        value: null,
        resetId: resetCounter + 1,
      };
    });

    setPreselectMap(clearedMap);
    onFiltersChange?.([]);
  };

  /**
   * The function `getGeneralDropdowns` asynchronously fetches dropdown configurations for each filter in
   * a list and updates the state with the results.
   */
  const getGeneralDropdowns = async () => {
    await Promise.all(
      filtersList.map(async (filter) => {
        const response = await dropdownConfig(filter.id);
        if (response.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
          setFiltersResult((prev) => ({
            ...prev,
            [filter.columnName]: {
              ...response.data.load,
            },
          }));
        }
      })
    );
  };

  /* The `useEffect` hook you provided is responsible for setting up initial preselected filters based on
the `preselectValues` defined in the `filtersList` array. Here's a breakdown of what it does: */
  useEffect(() => {
    const initialPreselects = {};
    filtersList.forEach((filter) => {
      if (filter.preselectValues) {
        initialPreselects[filter.columnName] = {
          value: filter.preselectValues,
          resetId: resetCounter,
        };
      }
    });
    if (Object.keys(initialPreselects).length > 0) {
      setPreselectMap((prev) => ({ ...prev, ...initialPreselects }));

      const preselectedFiltersForParent = filtersList
        .filter((f) => f.preselectValues)
        .map((f) => ({
          columnName: f.columnName,
          value: f.preselectValues,
        }));

      if (preselectedFiltersForParent.length > 0) {
        onFiltersChange?.(preselectedFiltersForParent);
      }
    }
  }, [filtersList]);

  useEffect(() => {
    getGeneralDropdowns();
  }, []);

  return (
    <section className="filters-and-sorting">
      <div
        className={`filters-and-sorting-general-filters ${
          viewFilters ? "open" : ""
        }`}
      >
        {/*The code snippet you provided is mapping over the `filtersList` array to render a list of dropdown
components based on the filters defined in the list. Here's a breakdown of what the code is doing: */}
        {filtersList.map((filter, idx) => (
          <>
            <div key={idx} className="general-dropdown">
              {filtersResult[filter.columnName] && (
                <Dropdowns
                  isStatic={filter?.isStatic ?? false}
                  staticData={filter?.staticData ?? null}
                  data={filtersResult[filter.columnName]}
                  parameters={filter?.parameters ?? null}
                  onApplySelection={(selectedItems) =>
                    applyFilterSelection(
                      filter.columnName,
                      filter.columnSelection,
                      selectedItems
                    )
                  }
                  handleClear={() => clearFilter(filter.columnName)}
                  preSelectValue={preselectMap[filter.columnName]?.value}
                  resetId={preselectMap[filter.columnName]?.resetId}
                />
              )}
            </div>
          </>
        ))}
        <div className="clear-btn-wrapper">
          <button
            onClick={clearAllFilters}
            className="sorting-and-filters-clear-btn"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* sorting code bellow */}
     {/* This part of the code is responsible for rendering the sorting component within the
     FiltersAndSorting component. Here's a breakdown of what it does:  */}
      <div className="sorting-component-fs">
        <div className="columns">
          {Object.entries(properties).map(([key, label]) => {
            const filterDef = filtersList.find((f) => f.columnName === key);
            return (
              <div key={key} className="column">
                {columnFilters?.hasOwnProperty(key) && filtersResult[key] ? (
                  <Dropdowns
                    isStatic={filterDef.isStatic ?? false}
                    staticData={filterDef.staticData ?? null}
                    data={filtersResult[key]}
                    parameters={filterDef?.parameters ?? null}
                    onApplySelection={(selectedItems) =>
                      applyFilterSelection(
                        key,
                        filtersList.find((f) => f.columnName === key)
                          ?.columnSelection || "",
                        selectedItems
                      )
                    }
                    handleClear={() => clearFilter(key)}
                    preSelectValue={preselectMap[key]?.value}
                    resetId={preselectMap[key]?.resetId}
                  >
                    <Tooltip title={`Click To Open ${label} Filter`} arrow>
                      <b
                        style={{
                          textDecoration: "underline",
                          color: "#4EC4EF",
                        }}
                      >
                        {label}
                      </b>
                    </Tooltip>
                  </Dropdowns>
                ) : (
                  <b style={{ cursor: "default" }}>{label}</b>
                )}
                {!hideArrows && (
                  <div className="arrows" onClick={() => loopKey(key)}>
                    <Tooltip
                      arrow
                      title={
                        isAsc(key)
                          ? "Ascending"
                          : isDesc(key)
                          ? "Descending"
                          : ""
                      }
                    >
                      <i
                        className={`arrow-button ${
                          isAsc(key)
                            ? "icon-arrowup"
                            : isDesc(key)
                            ? "icon-arrowdown"
                            : "icon-hyphen"
                        } ${isAsc(key) || isDesc(key) ? "active" : ""}`}
                      >
                        {showHeaderOrder && order.includes(key) && (
                          <u>{order.indexOf(key) + 1}</u>
                        )}
                      </i>
                    </Tooltip>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
